import { useState } from "react"
import { View } from "react-native"

import { Dialog as ThemedDialog } from "@rn-vui/themed"
import { useRouter } from "expo-router"

import { Button, Text } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAppTheme } from "src/hooks/useAppTheme"

/**
 * Shown when the active sponsor/channel was deleted out from under the user
 * (HTTP 401 INVALID_SPONSOR_CONTEXT / SPONSOR_NOT_ACTIVE_IN_ENVIRONMENT).
 *
 * This is a TERMINAL state: there is no session to resume and no token that
 * will ever validate, so we deliberately do NOT offer "Resume Here" (which
 * would re-mint against the dead sponsor and loop). Instead the user can:
 *   - "Choose another channel" → keep Cognito auth, clear this tab's
 *     channel/session state, and route to the channel-selection screen.
 *   - "Log out" → clear local session state and return to the login screen.
 *
 * Both actions are offered on every platform (unlike the takeover dialog,
 * which hides "Log out" on web): the channel is genuinely gone, so there is no
 * other-tab session that a logout would unfairly disrupt that the user is not
 * already losing.
 */
export function SponsorInactiveDialog() {
  const { theme } = useAppTheme()
  const router = useRouter()
  const { isSponsorInactive, recoverFromSponsorInactive, localLogoutAfterDeactivation } = useAuth()
  const [busy, setBusy] = useState<"switch" | "logout" | null>(null)

  const onChooseAnotherChannel = () => {
    setBusy("switch")
    recoverFromSponsorInactive()
    router.replace("/setup/select-sponsor")
  }

  const onLogout = () => {
    setBusy("logout")
    localLogoutAfterDeactivation()
    router.replace("/unauth/login")
  }

  return (
    <ThemedDialog
      isVisible={isSponsorInactive}
      onBackdropPress={() => {}}
      overlayStyle={{
        backgroundColor: theme.colors.background,
        borderRadius: 12,
        padding: 24,
        maxWidth: 400,
        width: "90%",
      }}
    >
      <ThemedDialog.Title
        title="Channel no longer available"
        titleStyle={{
          color: theme.colors.text,
          fontWeight: "600",
          fontSize: 18,
          marginBottom: 12,
        }}
      />
      <Text style={{ marginBottom: 16, color: theme.colors.text }}>
        This Sidekick channel is no longer available. Choose another channel or log out.
      </Text>
      <View style={{ flexDirection: "row", gap: 12, flexWrap: "wrap" }}>
        <View style={{ flex: 1, minWidth: 120 }}>
          <Button
            text="Choose another channel"
            preset="reversed"
            onPress={onChooseAnotherChannel}
            disabled={busy !== null}
          />
        </View>
        <View style={{ flex: 1, minWidth: 120 }}>
          <Button text="Log out" preset="subtle" onPress={onLogout} disabled={busy !== null} />
        </View>
      </View>
    </ThemedDialog>
  )
}
